import { ArrowRightIcon, ChevronDownIcon } from '@heroicons/react/outline';
import Link from 'next/link';
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';

import clsxm from '@/lib/clsxm';

import { useAnalytics } from '@/contexts/Analytics';

/**
 * Colour roles of the dashboard sections. Light mode uses the same palette as
 * the /chatbots landing (`src/components/agents/primitives.tsx`); dark mode
 * stays on the app's slate so it matches the sidebar.
 */
export const tone = {
  page: 'bg-[#f8f8fb] dark:bg-slate-900',
  surface: 'bg-white dark:bg-slate-800/70',
  line: 'ring-1 ring-[#0f0f28]/[0.07] dark:ring-white/[0.08]',
  ink: 'text-[#10101c] dark:text-slate-100',
  body: 'text-[#56566d] dark:text-slate-300',
  quiet: 'text-[#6f6f86] dark:text-slate-400',
  soft: 'bg-[#f4f4fb] dark:bg-white/[0.05]',
};

export const focusRing =
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600';

/** A raised card: white on the page tone, with a faint indigo-tinted shadow. */
export const card = clsxm(
  tone.surface,
  tone.line,
  'shadow-[0_1px_2px_rgba(16,16,40,0.04),0_12px_32px_-18px_rgba(49,46,129,0.22)] dark:shadow-none',
);

export const chip = clsxm(
  'inline-flex shrink-0 items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[13px] font-medium transition hover:ring-[#0f0f28]/20 dark:hover:ring-white/25',
  tone.surface,
  tone.line,
  tone.ink,
  focusRing,
);

export function SectionHead({
  title,
  action,
  href,
  onAction,
  expanded,
}: {
  title: string;
  action?: ReactNode;
  href?: string;
  onAction?: () => void;
  /**
   * Set for an action that opens the section in place ("Show all" / "Show
   * less"): it then shows a chevron, not the arrow of a link elsewhere.
   */
  expanded?: boolean;
}) {
  const actionClass = clsxm(
    'group inline-flex shrink-0 items-center gap-1 rounded text-[13px] font-medium transition-colors hover:text-primary-700 dark:hover:text-primary-300',
    tone.body,
    focusRing,
  );
  const arrow =
    expanded === undefined ? (
      <ArrowRightIcon className='h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 rtl:rotate-180 rtl:group-hover:-translate-x-0.5' />
    ) : (
      <ChevronDownIcon
        className={clsxm(
          'h-4 w-4 transition-transform',
          expanded && 'rotate-180',
        )}
      />
    );

  return (
    <div className='mb-3 flex items-end justify-between gap-4'>
      <h2
        className={clsxm(
          'text-[17px] font-semibold tracking-[-0.01em]',
          tone.ink,
        )}
      >
        {title}
      </h2>
      {action &&
        (href ? (
          <Link href={href} onClick={onAction} className={actionClass}>
            {action}
            {arrow}
          </Link>
        ) : (
          <button
            type='button'
            onClick={onAction}
            aria-expanded={expanded}
            className={actionClass}
          >
            {action}
            {arrow}
          </button>
        ))}
    </div>
  );
}

/**
 * Click tracking for the dashboard body. Autocapture is off in PostHog, so
 * these events are the only way to see which sections people use. Other
 * pages in this kit pass their own event name (/presets: `Tools Click`).
 */
export function useDashboardTrack(event = 'Dashboard Click') {
  const analytics = useAnalytics();
  return useCallback(
    (section: string, target: string, extra?: Record<string, unknown>) => {
      analytics.track(event, { section, target, ...extra });
    },
    [analytics, event],
  );
}

/**
 * Renders `children` only once the placeholder scrolls near the visible part
 * of the page, so below-the-fold sections don't download their code and media
 * for the half of visitors who leave within seconds. Keep a min-height on the
 * placeholder (via `className`) so nothing jumps when it fills in.
 */
export function LazyMount({
  children,
  className,
  rootMargin = '600px 0px',
}: {
  children: ReactNode;
  className?: string;
  rootMargin?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (visible) return;
    const el = ref.current;
    if (!el) return;
    if (!('IntersectionObserver' in window)) {
      setVisible(true);
      return;
    }
    // The dashboard scrolls inside <main>, and a rootMargin only stretches
    // the root, so observe against that scroll container to preload early.
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { root: el.closest('main'), rootMargin },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [visible, rootMargin]);

  // The min-height only holds the space until the real content arrives.
  return (
    <div ref={ref} className={visible ? undefined : className}>
      {visible ? children : null}
    </div>
  );
}
